import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { markSpotlightSeen, dismissSpotlight, trySpotlight } from "@/api/featureSpotlights";
import { spotlightTarget } from "@touchworks/shared/platform/spotlightTargets.js";
import { resolveDestination } from "./resolveDestination";
import { showsOnHome } from "./spotlightSurface";
import { CANDIDATES_QUERY_KEY, useCandidates } from "./useCandidates";

const allowEveryTarget = () => true;

/**
 * O destaque de lançamento da tela inicial: a novidade que a pessoa vê como cartão grande abaixo dos atalhos do Lucas.
 *
 * É a mesma novidade do cartão ao lado do menu (mesmo cadastro, mesmo público, mesma prioridade, mesmo funil), só desenhada
 * em outra superfície, então lê a MESMA consulta de candidatos (uma ida ao servidor) e grava nas mesmas rotas. Regras:
 *
 * - Escolhe a primeira da lista (já ordenada por prioridade) que aparece na tela inicial (`surface` `home` ou `both`) e cujo
 *   item de menu a pessoa de fato enxerga. As puladas nunca viram "visto".
 * - Um por carregamento: depois de fechar ou experimentar, nenhuma outra assume o lugar até o próximo carregamento. Uma lista
 *   nova (a busca refaz a cada 5 min) não troca o cartão que já está na tela; se ele sai da lista (pausada, encerrada), some.
 * - "Visto" é gravado uma vez, quando o cartão está na tela (`visible`): a tela inicial esconde o corpo enquanto o Lucas
 *   conversa, e isso não conta como visto.
 * - A interação é uma só por novidade: fechar aqui a tira também do menu (e vice-versa). Depois de gravar, a lista é relida
 *   para a outra superfície sumir sem esperar.
 * - Falha de rede nunca atrapalha a pessoa: o cartão some do mesmo jeito, e a dispensa é tentada duas vezes.
 *
 * @param {{
 *   environment: { visibleNavKeys: string[], resolveAccountPath: (subPath: string) => string, canSeeTarget?: (target: object) => boolean } | null | undefined,
 *   visible?: boolean,
 * }} params `environment` vem do layout (o mesmo que alimenta o cartão do menu); sem ele (embed, sem conta) nada aparece
 */
export function useHomeSpotlight({ environment, visible = true }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enabled = Boolean(environment);
  const { data } = useCandidates(enabled);

  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [pinnedId, setPinnedId] = useState(null);
  const seenIds = useRef(new Set());
  const resolving = useRef(new Set());

  const keysSignature = (environment?.visibleNavKeys ?? []).join("|");
  const canSeeTarget = environment?.canSeeTarget ?? allowEveryTarget;
  const cards = data?.cards;

  const spotlight = useMemo(() => {
    if (!enabled || resolvedIds.size > 0) return null;
    const keys = keysSignature.split("|");
    const eligible = (cards ?? []).filter((card) => {
      if (!showsOnHome(card) || !keys.includes(card.anchorNavKey)) return false;
      const target = card.targetKey ? spotlightTarget(card.targetKey) : null;
      return !target || canSeeTarget(target);
    });
    const pinned = eligible.find((card) => card.id === pinnedId);
    if (pinned) return pinned;
    if (pinnedId && !(cards ?? []).some((card) => card.id === pinnedId)) return null;
    return eligible[0] ?? null;
  }, [enabled, resolvedIds, cards, keysSignature, pinnedId, canSeeTarget]);

  useEffect(() => {
    if (spotlight && spotlight.id !== pinnedId) setPinnedId(spotlight.id);
  }, [spotlight, pinnedId]);

  useEffect(() => {
    if (!spotlight || !visible || seenIds.current.has(spotlight.id)) return;
    seenIds.current.add(spotlight.id);
    markSpotlightSeen(spotlight.id).catch(() => {});
  }, [spotlight, visible]);

  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: CANDIDATES_QUERY_KEY }), [queryClient]);

  // Clique duplo, ou os dois botões em sequência, só valem uma vez.
  const resolveOnce = useCallback((id, run) => {
    if (resolving.current.has(id)) return;
    resolving.current.add(id);
    setResolvedIds((previous) => new Set(previous).add(id));
    run();
  }, []);

  const dismiss = useCallback(() => {
    if (!spotlight) return;
    resolveOnce(spotlight.id, () => {
      const save = () => dismissSpotlight(spotlight.id);
      save().catch(save).then(refresh, () => {});
    });
  }, [spotlight, resolveOnce, refresh]);

  const tryIt = useCallback(() => {
    if (!spotlight) return;
    resolveOnce(spotlight.id, () => {
      // O destino sai do próprio cartão e é aplicado dentro do clique: abrir uma aba depois de aguardar a API
      // cai no bloqueador de pop-ups, e a navegação não precisa esperar a gravação.
      const destination = resolveDestination(
        { ...spotlight.ctaTarget, anchorNavKey: spotlight.anchorNavKey, targetKey: spotlight.targetKey },
        environment.resolveAccountPath,
      );
      if (destination?.type === "external") window.open(destination.url, "_blank", "noopener,noreferrer");
      else if (destination) navigate(destination.to);
      // Depois de gravar, relê a lista: com um recurso dentro da tela, o servidor já devolve o ponteiro armado.
      trySpotlight(spotlight.id).then(refresh).catch(() => {});
    });
  }, [spotlight, resolveOnce, environment, navigate, refresh]);

  return { spotlight, dismiss, tryIt };
}
